import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaInstagram, FaFacebookF } from "react-icons/fa";
import Footer from "../../components/layout/Footer";
import axiosInstance from "../../utils/axiosInstance";

const QuotationView = () => {
  const { id } = useParams();
  const [quotationData, setQuotationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch quotation data from API
  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/quatation/${id}`);
        if (response.data.Status === 1) {
          console.log('Quotation data:', response.data.quatation);
          console.log('Lead data:', response.data.quatation.lead);
          console.log('Items data:', response.data.quatation.items);
          setQuotationData(response.data.quatation);
        } else {
          setError(response.data.Message || 'Failed to fetch quotation');
        }
      } catch (err) {
        setError('Failed to fetch quotation data');
        console.error('Error fetching quotation:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuotationData();
    }
  }, [id]);

  const [rows, setRows] = useState([]);

  const [commercialRows, setCommercialRows] = useState([]);

  // Update rows when quotation data is loaded
  useEffect(() => {
    if (quotationData && quotationData.items) {
      const formattedRows = quotationData.items.map(item => ({
        itemName: typeof item.name === 'string' ? item.name : (item.name?.name || item.item?.name || ""),
        variant: typeof item.variant === 'string' ? item.variant : (item.variant?.name || ""),
        companyName: item.variant?.companyName || "",
        description: item.variant?.description || "",
        unit: typeof item.variant?.unit === 'string' ? item.variant?.unit : (item.variant?.unit?.name || ""),
        quantity: item.qty || "",
        rate: item.variant?.rate || item.rate || "",
        taxRate: item.variant?.gst || item.gst || "",
        taxAmount: ((Number(item.qty || 0) * Number(item.variant?.rate || item.rate || 0) * Number(item.variant?.gst || item.gst || 0)) / 100).toFixed(2) || "0.00",
        total: (Number(item.qty || 0) * Number(item.variant?.rate || item.rate || 0) * (1 + (Number(item.variant?.gst || item.gst || 0) || 0) / 100)).toFixed(2) || "0.00",
      }));
      setRows(formattedRows);
    }
  }, [quotationData]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid py-4 px-3">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading quotation...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container-fluid py-4 px-3">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error!</h4>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!quotationData) {
    return (
      <div className="container-fluid py-4 px-3">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">No Data!</h4>
              <p>No quotation data found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const headerStyle = {
    backgroundColor: '#526D82',
    color: 'white',
    padding: '12px 15px',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  };

  // Calculate totals from API data
  const subTotal = rows.reduce((acc, row) => acc + parseFloat(row.total || 0), 0);
  const meterChargeTotal = quotationData?.commercialOffer?.particulars?.[0]?.total || 0;
  const subsidyTotal = quotationData?.commercialOffer?.subsidies?.[0]?.total || 0;

  // Calculate GST and Margin amounts from quotation data
  const discountAmount = Number(quotationData?.discount || 0);
  const marginPercentage = Number(quotationData?.margin || 0);
  const gstPercentage = Number(quotationData?.gstRate || 18);
  const adjustmentAmount = Number(quotationData?.adjustment || 0);

  // Calculate margin amount
  const marginAmount = (subTotal * marginPercentage) / 100;

  // Calculate GST amount on (subtotal - discount + margin)
  const baseForGst = subTotal - discountAmount + marginAmount;
  const gstAmount = (baseForGst * gstPercentage) / 100;

  // Calculate grand total: subtotal - discount + margin + GST + adjustment
  const grandTotal = subTotal - discountAmount + marginAmount + gstAmount + adjustmentAmount;

  return (
    <div className="container-fluid py-4 px-3">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">View Quotation</h1>
        <a href="/user/quotation" className="text-decoration-none text-muted">
          <span className="me-2">&larr;</span>Quotation
        </a>
      </div>

      {/* Quotation Card */}
      <div className="item-form rounded-4 p-4" style={{ background: "#f6f6f6", border: "1.5px solid #bbbfd2" }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <img src="/assets/images/solar.png" alt="Logo" className="img-fluid" style={{ width: "160px" }} />
          <div className="text-end" style={{ fontSize: "14px", lineHeight: 1.6 }}>
            <div><b>Date:</b> {formatDate(quotationData.quotationDate)}</div>
            <div><b>Valid Date:</b> {formatDate(quotationData.validDate)}</div>
            <div><b>Quotation No:</b> {String(quotationData.code || 'N/A')}</div>
          </div>
        </div>

        <hr className="mb-3" style={{ borderTop: "0.5px solid #BBBFD2" }} />

        <h2 className="text-center mt-4 mb-4" style={{ fontSize: "20px", color: "#3F3F46" }}>
          {String(quotationData.title || 'Solar Rooftop Power Plant')}
        </h2>

        {/* Recipient */}
        <div className="mb-3" style={{ fontSize: "14px", lineHeight: 1.8 }}>
          To,<br />
          <span className="fw-bold">
            {(() => {
              const lead = quotationData.lead;
              if (!lead) return 'N/A';
              
              // If lead.name is a string, use it
              if (typeof lead.name === 'string' && lead.name.trim()) {
                return lead.name;
              }
              
              // If lead.name is an object with name property
              if (lead.name && typeof lead.name === 'object' && lead.name.name) {
                return lead.name.name;
              }
              
              // Try firstName + lastName
              if (lead.firstName && lead.lastName) {
                return `${lead.firstName} ${lead.lastName}`;
              }
              
              // Try individual names
              if (lead.firstName) return lead.firstName;
              if (lead.lastName) return lead.lastName;
              
              return 'N/A';
            })()}
          </span><br />
          {String(quotationData.lead?.address || 'Address not available')}<br />
          {quotationData.lead?.city && `${String(quotationData.lead.city?.name || quotationData.lead.city)}, `}
          {String(quotationData.lead?.state?.name || quotationData.lead?.state || '')}<br />
          Mo. {String(quotationData.lead?.mobile || quotationData.lead?.phone || 'Mobile not available')}
        </div>
        {quotationData.showplantconfiguraton && quotationData?.plantconfiguraton && (
        <div className="mb-4">
          <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
            <div className="border-bottom text-center fw-bold py-2 quotation-view">
              Plant Configuration
            </div>
            <div className="p-3">
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: quotationData.plantconfiguraton || 'No plant configuration available.' 
                }} 
              />
            </div>
          </div>
        </div>
)}
        
        {/* Material Table */}
        <div className="card border-0 rounded-4 shadow-sm mt-4 bg-white">
          <div className="border-bottom text-center fw-bold py-2 quotation-view rounded-2">
              Bill Of Material
          </div>
          <div className="card-body p-4">
            <div className="table-responsive">
              <table className="table align-middle text-center mb-0" style={{ borderCollapse: "separate", borderSpacing: "0 8px", width: "100%" }}>
                <thead className="border-bottom px-3 py-2 fw-bold text-center" style={{ backgroundColor: "#bbbfd2", color: "#3F3F46" }}>
                  <tr>
                    <th style={{ borderTopLeftRadius: "8px", width: "60px" }}>Sr. No</th>
                    <th>Item</th>
                    <th>Variant</th>
                    <th>Company Name</th>
                    <th>Description</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>Rate (₹)</th>
                    <th>Tax Rate (%)</th>
                    <th>Tax Amount (₹)</th>
                    <th style={{ borderTopRightRadius: "8px" }}>Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="bg-white shadow-sm">
                      <td className="fw-semibold">{idx + 1}</td>
                      <td>{String(row.itemName || '')}</td>
                      <td>{String(row.variant || '')}</td>
                      <td>{String(row.companyName || '')}</td>
                      <td>{String(row.description || '')}</td>
                      <td>{String(row.unit || '')}</td>
                      <td className="text-center">{String(row.quantity || '')}</td>
                      <td className="text-center">₹{String(row.rate || '0')}</td>
                      <td className="text-center">{String(row.taxRate || '0')}%</td>
                      <td className="text-center">₹{String(row.taxAmount || '0.00')}</td>
                      <td className="text-center fw-semibold">₹{String(row.total || '0.00')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="d-flex flex-column align-items-end mt-4 fs-5">
              <div className="mb-2">Sub Total: <span className="fw-semibold">₹{subTotal.toFixed(2)}</span></div>
              <div className="mb-2">GST: <span className="fw-semibold">₹{gstAmount.toFixed(2)}</span></div>
              <div className="mb-2">Margin: <span className="fw-semibold">₹{marginAmount.toFixed(2)}</span></div>
              <div className="fw-bold fs-4 text-primary mt-2">Grand Total: <span>₹{grandTotal.toFixed(2)}</span></div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="border rounded-4 overflow-hidden">
            <div
              className="border-bottom text-center fw-bold py-2"
              style={{ backgroundColor: "#5766A1", color: "#FFFFFF" }}
            >
              Commercial Offer
            </div>

            <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
              <div className="border-bottom text-center fw-bold py-2 quotation-view">
                Meter Charge Calculation
              </div>

              {/* ===== Table Section ===== */}
              <div className="p-3">
                <table
                  className="table table-bordered text-center mb-0 border overflow-hidden rounded-1"
                  style={{tableLayout: "fixed"}}
                >
                  <thead style={{ backgroundColor: "#F7F8FB" }}>
                    <tr>
                      <th style={{ width: "10%", textAlign: "center" }}>Sr No.</th>
                      <th style={{ textAlign: "center" }}>Description</th>
                      <th style={{ width: "25%", textAlign: "center" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Display particulars from API data */}
                    {quotationData?.commercialOffer?.particulars?.[0]?.items?.length > 0 ? (
                      quotationData.commercialOffer.particulars[0].items.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{String(item.description || 'Meter Charge')}</td>
                          <td>₹ {Number(item.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>1</td>
                        <td>Meter Charge</td>
                        <td>₹ {Number(meterChargeTotal || 0).toFixed(2)}</td>
                      </tr>
                    )}

                    {/* Total Row */}
                    <tr
                      className="fw-semibold"
                      style={{ borderTop: "2px solid #D6D8E3", backgroundColor: "#f1f3f6" }}
                    >
                      <td colSpan={2} className="text-end px-3">
                        Total Meter Charge
                      </td>
                      <td className="text-end px-3">₹ {meterChargeTotal.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
              <div className="border-bottom text-center fw-bold py-2 quotation-view">
                Subsidy Calculation As Per Govt. Subsidy Structure
              </div>

              {/* ===== Table Section ===== */}
              <div className="p-3">
                <table
                  className="table table-bordered text-center mb-0 border overflow-hidden rounded-1"
                  style={{tableLayout: "fixed"}}
                >
                  <thead style={{ backgroundColor: "#F7F8FB" }}>
                    <tr>
                      <th style={{ width: "10%", textAlign: "center" }}>Sr No.</th>
                      <th style={{ textAlign: "center" }}>Description</th>
                      <th style={{ width: "25%", textAlign: "center" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Display subsidies from API data */}
                    {quotationData?.commercialOffer?.subsidies?.[0]?.items?.length > 0 ? (
                      quotationData.commercialOffer.subsidies[0].items.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{String(item.description || 'Government Subsidy Amount')}</td>
                          <td>₹ {Number(item.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>1</td>
                        <td>Government Subsidy Amount</td>
                        <td>₹ {Number(subsidyTotal || 0).toFixed(2)}</td>
                      </tr>
                    )}

                    {/* Total Subsidy Row */}
                    <tr
                      className="fw-semibold"
                      style={{ borderTop: "2px solid #D6D8E3", backgroundColor: "#f1f3f6" }}
                    >
                      <td colSpan={2} className="text-end px-3">
                        Total Subsidy Amount
                      </td>
                      <td className="text-end px-3">
                        <strong>₹ {subsidyTotal.toFixed(2)}</strong>
                      </td>
                    </tr>

                    {/* After Subsidy Row */}
                    <tr className="border">
                      <td colSpan={2} className="text-end px-3 text-muted" style={{ color: "#12B76A" }}>
                        <h4>After Subsidy</h4>
                      </td>
                      <td className="text-end px-3 text-muted" style={{ color: "#12B76A" }}>
                        <strong><h4>₹ {(grandTotal - subsidyTotal).toFixed(2)}</h4></strong>
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>

          </div>


          {/* ===== Payment Terms Section ===== */}
          <div className="mb-4">
            <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
              <div
                className="border-bottom text-center fw-bold py-2 quotation-view">
                Payment Terms
              </div>
              <div className="p-3">
                <table
                  className="table table-bordered text-center mb-0 border overflow-hidden rounded-1"
                  style={{
                    tableLayout: "fixed",
                  }}
                >
                  <thead style={{ backgroundColor: "#F7F8FB" }}>
                    <tr>
                      <th style={{ width: "15%", textAlign: "center" }}>Percentage</th>
                      <th style={{ textAlign: "center" }}>Description</th>
                      <th style={{ width: "25%", textAlign: "center" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Display payment terms from API data */}
                    {quotationData?.commercialOffer?.paymentTerms?.length > 0 ? (
                      quotationData.commercialOffer.paymentTerms[0]?.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{String(item.percentage || '')}%</td>
                          <td>{String(item.description || 'Payment Term')}</td>
                          <td>₹ {Number(item.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="text-center text-muted">No payment terms available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ===== Warranty Section ===== */}
          {quotationData?.showWarranty && (
            <div className="mb-4">
              <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
                <div className="border-bottom text-center fw-bold py-2 quotation-view">
                  Warranty
                </div>

                <div className="p-3">
                  <div dangerouslySetInnerHTML={{ __html: quotationData.warranty || 'No warranty information available.' }} />
                </div>
              </div>
            </div>
          )}


          {/* ===== Scope of Work Section ===== */}
          {quotationData?.showScope && (
            <div className="mb-4">
              <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
                <div
                  className="border-bottom text-center fw-bold py-2 quotation-view">
                  Scope Of Work
                </div>

                <div className="p-3">
                  <div dangerouslySetInnerHTML={{ __html: quotationData.scope || 'No scope of work information available.' }} />
                </div>
              </div>
            </div>
          )}

          {/* ===== Commercial Terms & Conditions Section ===== */}
          {quotationData?.showCommercialTermsAndConditions && (
            <div className="mb-4">
              <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
    
                <div className="border-bottom text-center fw-bold py-2 quotation-view">
                  Commercial Terms & Conditions
                </div>
                <div className="p-3">
                  <div dangerouslySetInnerHTML={{ __html: quotationData.commercialTermsAndConditions || 'No commercial terms and conditions available.' }} />
                </div>
              </div>
            </div>
          )}

          {/* ===== Residential Terms & Conditions Section ===== */}
          {quotationData?.showResidentialTermsAndConditions && (
            <div className="mb-4">
              <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
                
                <div className="border-bottom text-center fw-bold py-2 quotation-view">
                  Residential Terms & Conditions
                </div>
                <div className="p-3">
                  <div dangerouslySetInnerHTML={{ __html: quotationData.residentialTermsAndConditions || 'No residential terms and conditions available.' }} />
                </div>
              </div>
            </div>
          )}

          {/* ===== Privacy Policy Section ===== */}
          {quotationData?.showPrivacyPolicy && (
            <div className="mb-4">
              <div className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title">
                <div className="border-bottom text-center fw-bold py-2 quotation-view">
                  Privacy Policy
                </div>

                <div className="p-3">
                  <div dangerouslySetInnerHTML={{ __html: quotationData.privacyPolicy || 'No privacy policy information available.' }} />
                </div>
              </div>
            </div>
          )}

          <div className="container my-4">
            <div className="row g-3">

              {/* Left Section: Bank Details */}
              <div className="col-md-6">
                <div className="border rounded-3 overflow-hidden h-100">
                  <div className="text-center fw-bold py-2" style={{ backgroundColor: '#5766A1', color: '#fff' }}>
                    Bank Details
                  </div>
                  <div className="p-3">
                    <div className="d-flex justify-content-between border-bottom py-2">
                      <strong>Bank Name</strong>
                      <span>HDFC Bank</span>
                    </div>
                    <div className="d-flex justify-content-between border-bottom py-2">
                      <strong>Account Name</strong>
                      <span>ABC Pvt Ltd</span>
                    </div>
                    <div className="d-flex justify-content-between border-bottom py-2">
                      <strong>Account Number</strong>
                      <span>50100XXXXX1234</span>
                    </div>
                    <div className="d-flex justify-content-between py-2">
                      <strong>GST Number</strong>
                      <span>22ABCDE1234F1Z5</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section: UPI QR */}
              <div className="col-md-6">
                <div className="border rounded-3 overflow-hidden h-100">
                  <div className="text-center fw-bold py-2" style={{ backgroundColor: '#5766A1', color: '#fff' }}>
                    UPI QR Image
                  </div>
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px', backgroundColor: '#f8f9fa' }}>
                    <span className="text-muted">QR Code Here</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="mb-4">
            {/* ===== Outer Rounded Border Box ===== */}
            <div
              className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title"
            >
              <div className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                  {/* Left Side */}
                  <div className="text-start pe-3" style={{ flex: 1 }}>
                    <h5 className="mb-2">Innoveza Software</h5>
                    <h5 className="mb-2">Authorized Signatory</h5>
                    <h5 className="mb-0">Date: _______</h5>
                  </div>

                  {/* Right Side */}
                  <div className="text-end ps-3" style={{ flex: 1 }}>
                    <h5 className="mb-2">Customer Name</h5>
                    <h5 className="mb-2">Customer Signatory</h5>
                    <h5 className="mb-0">Date: _______</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div
              className="border rounded-4 overflow-hidden mx-auto my-4 quotation-title"
            >
              {/* ===== List Section ===== */}
              <div className="p-3">
                <h3 className="mb-3">Follow Us</h3>
                <div className="d-flex gap-3">
                  {/* Instagram Icon */}
                  <a
                    href="https://www.instagram.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ color: "#E1306C" }}
                  >
                    <FaInstagram size={30} />
                  </a>

                  {/* Facebook Icon */}
                  <a
                    href="https://www.facebook.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ color: "#1877F2" }}
                  >
                    <FaFacebookF size={30} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default QuotationView;
